/* @flow */
import idx from 'idx';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { filter } from 'fuzzaldrin';
import Text from 'components/Text';
import { graphql } from 'react-apollo';
import Input from 'components/Input';
import Dialog from 'components/Dialog';
import KeyConstants from 'constants/KeyConstants';
import { withRouter } from 'app/context/Router';
import NAVIGATION_QUERY from 'queries/navigationList.gql';
import ScrollableArea from 'components/ScrollableArea';
import GroupQuickSwitcherItem from 'groups/components/GroupQuickSwitcherItem';

import './styles/GroupQuickSwitcher.scss';

type State = {
  query: string,
  selection: number,
};

type Props = {
  data: *,
  router: *,
  onRequestHide: Function,
};

class GroupQuickSwitcher extends React.Component<Props, State> {
  state: State = {
    query: '',
    selection: 0,
  };

  componentDidMount() {
    if (this.refs.input) {
      setTimeout(this.refs.input.focus, 0);
    }

    document.addEventListener('keydown', this.handleKeyPressed);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const newSelection = this.state.selection !== prevState.selection;

    if (newSelection && this.refs.scrollArea) {
      const node = ReactDOM.findDOMNode(this.refs[`result-${this.state.selection}`]);
      this.refs.scrollArea.scrollIntoView(node);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyPressed);
  }

  handleKeyPressed = (event: *) => {
    const items = this.getItems(this.state.query);

    if (items.length === 0) {
      return;
    }

    switch (event.keyCode) {
      case KeyConstants.ENTER:
        event.preventDefault();
        this.navigate(this.state.selection);
        break;
      case KeyConstants.DOWN:
        event.preventDefault();
        this.setState({
          selection: this.state.selection + 1 === items.length ? 0 : this.state.selection + 1,
        });
        break;
      case KeyConstants.UP:
        event.preventDefault();
        this.setState({
          selection: this.state.selection === 0 ? items.length - 1 : this.state.selection - 1,
        });
        break;
      default:
        break;
    }
  };

  handleQueryChange = (event: *) => this.setState({ query: event.target.value, selection: 0 });

  handleOnMouseOver = (index: *) => {
    if (this.state.selection !== index) {
      this.setState({ selection: index });
    }
  };

  getItems(query: string) {
    const sections = idx(this.props.data, _ => _.me.navigationSections);

    if (!Array.isArray(sections)) {
      return [];
    }

    const items: *[] = [].concat(...sections.map(section => section.items));

    if (!query || query.trim().length === 0) {
      return items;
    }

    return filter(items, query, { key: 'displayName' });
  }

  navigate(index: number) {
    const item = this.getItems(this.state.query)[index];

    if (!item) {
      return;
    }

    if (item.__typename === 'Class') {
      this.props.router.push(`/classes/${item.name}`);
    }

    if (item.__typename === 'Organization') {
      this.props.router.push(`/organizations/${item.id}`);
    }

    if (item.__typename === 'NavigationSectionItemRootClass') {
      this.props.router.push('/classes/r');
    }
  }

  render() {
    const items = this.getItems(this.state.query);

    return (
      <Dialog
        size="small"
        hideCloseButton
        className="GroupQuickSwitcher"
        onRequestHide={this.props.onRequestHide}>
        <React.Fragment>
          <div className="GroupQuickSwitcher-search">
            <Input
              noBorder
              ref="input"
              size="large"
              placeholder="Jump to..."
              onChange={this.handleQueryChange}
            />
          </div>
          {items.length > 0 && (
            <ScrollableArea
              ref="scrollArea"
              useBodyChildrenHeight
              className="GroupQuickSwitcher-items">
              {items.map((item, index) => (
                <a
                  ref={`result-${index}`}
                  key={item.uuid || item.displayName}
                  className="GroupQuickSwitcher-item"
                  onClick={() => this.navigate(index)}
                  onMouseMove={() => this.handleOnMouseOver(index)}>
                  <GroupQuickSwitcherItem item={item} selected={index === this.state.selection} />
                </a>
              ))}
            </ScrollableArea>
          )}
          {items.length === 0 && (
            <div className="GroupQuickSwitcher-nomatch">
              <Text element="div" className="GroupQuickSwitcher-nomatch-text">
                No matches found. Did you spell it correctly?
              </Text>
            </div>
          )}
          {items.length > 0 && (
            <div className="GroupQuickSwitcher-shortcuts">
              <Text>↑↓ to navigate</Text>
              <Text>↵ to select</Text>
              <Text>
                <Text weight="semibold">esc</Text> to dismiss
              </Text>
            </div>
          )}
        </React.Fragment>
      </Dialog>
    );
  }
}

export { GroupQuickSwitcher };
const withNavigation = graphql(NAVIGATION_QUERY);
export default withRouter(withNavigation(GroupQuickSwitcher));
